// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL
    ? marketplaceRootURL.replace(/\/$/, '')
    : `${typeof window !== 'undefined' ? window.location.origin : ' '}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  if (typeof window === 'undefined') return Promise.resolve({});
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = body
    ? { body: shouldSerializeBody ? serialize(body) : body }
    : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers
      ? { ...headers, 'x-platform': 'web' }
      : { 'Content-Type': 'application/transit+json', 'x-platform': 'web' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

const get = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.GET,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Recomputes the advertised price of any deal linked to this service, right
// after a merchant edits the service's price/duration — so the deal isn't
// left showing a stale discount until the listing/updated webhook catches
// up. See server/api/resync-deal-prices.js.
export const resyncDealPrices = listingId => {
  return post('/api/resync-deal-prices', { listingId });
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

//algolia-search api's.

/*
Sends a POST request to the /api/algolia/createObject endpoint, which likely creates a new object in an Algolia index
*/
export const createAlgoliaObject = body => {
  return post('/api/algolia/createObject', body);
};
/*
Sends a POST request to the /api/algolia/updateObject endpoint, which updates an existing object in an Algolia index.
*/
export const updateAlgoliaObject = body => {
  return post('/api/algolia/updateObject', body);
};
/*
Sends a POST request to the /api/algolia/partialUpdateObject endpoint, which performs a partial update on an object in an Algolia index. This is useful when only certain fields of the object need to be updated without affecting the others.
*/
export const partialUpdateAlgoliaObject = body => {
  return post('/api/algolia/partialUpdateObject', body);
};
/*
Sends a GET request to the /api/algolia/searchData endpoint, which searches the Algolia index and returns matching results based on the search query.
*/
// export const searchAlgoliaData = body => {
//   return post('/api/algolia/searchData', body);
// };

// export const getAlgoliaObject = body => {
//   return post('/api/algolia/getObject', body);
// };

export const onUpdateBusinessProfile = body => {
  return post('/api/update-business-profile', body);
};

export const handleDealsOnCloseListing = body => {
  return post('/api/create-handle-deals-on-close-listing', body);
};

export const onGetCategories = () => {
  return get('/api/categories');
};

export const onGetDealsCategoriesList = () => {
  return get('/api/deals-categories-list');
};

export const onGetStockImages = (categoryId, subcategoryId, options = {}) => {
  const params = new URLSearchParams();
  if (categoryId != null) params.set('categoryId', String(categoryId));
  if (subcategoryId != null) params.set('subcategoryId', String(subcategoryId));
  if (options.limit != null) params.set('limit', String(options.limit));
  const qs = params.toString();
  return get(`/api/image-library/stock-images${qs ? `?${qs}` : ''}`);
};

export const onGetMerchantImages = options => {
  const params = new URLSearchParams();
  if (options?.categoryId != null) params.set('categoryId', String(options.categoryId));
  if (options?.subcategoryId != null) params.set('subcategoryId', String(options.subcategoryId));
  if (options?.status) params.set('status', String(options.status));
  if (options?.limit != null) params.set('limit', String(options.limit));
  if (options?.offset != null) params.set('offset', String(options.offset));
  const qs = params.toString();
  return get(`/api/image-library/merchant/images${qs ? `?${qs}` : ''}`);
};

export const onCreateMerchantImage = body => {
  return post('/api/image-library/merchant/images', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const onUpdateMerchantImage = (id, body) => {
  return post(`/api/image-library/merchant/images/${id}/reupload`, JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });
};

// TAP PAYMENT API FUNCTIONS - COMMENTED OUT (Replaced with STC Bank)
// These functions are kept for reference but should not be used
// Use STC Bank endpoints instead: onInitiateStcAuthorize, onRetrieveStcPayment, onConfirmStcPayment

// TAP PAYMENT API FUNCTIONS
export const onCreatePayment = body => {
  return post('/api/create-payment', body);
};

export const onCreateCharge = body => {
  return post('/api/create-charge', body);
};

export const onCreateRefund = body => {
  return post('/api/create-refund', body);
};

export const onCreateLead = body => {
  return post('/api/create-lead', body);
};

export const onCreateEmergencyLead = body => {
  return post('/api/emergency-leads', body);
};

export const onCreateMerchant = body => {
  return post('/api/create-merchant', body);
};

// onCreateBusiness is Tap-specific for creating Tap business accounts
export const onCreateBusiness = body => {
  return post('/api/create-business', body);
};

// Merchant payout application functions - NOT Tap-specific, used for general merchant onboarding
// These store merchant application data in database (Tap fields are optional)
export const onCreateMerchantPayoutApplication = body => {
  return post('/api/create-merchant-payout-application', body);
};

export const onFetchMerchantPayoutApplication = body => {
  return post('/api/fetch-merchant-payout-application', body);
};

export const onUpdateMerchantPayoutApplication = body => {
  return post('/api/update-merchant-payout-application', body);
};

export const onFetchStcBankList = () => {
  return get('/api/stc-bank-list');
};

export const onFetchPayoutConfig = () => {
  return get('/api/payout-config');
};

export const onGeneratePaymetStatus = body => {
  return post('/api/payment-status', body);
};

export const onFetchPaymentStatus = charge_id => {
  return post('/api/payment-status?charge_id=' + charge_id);
};

export const onFetchCustomerCards = body => {
  return post('/api/fetch-customer-cards', body);
};

export const onCreateTokenForSavedCard = body => {
  return post('/api/create-token-for-saved-cards', body);
};
// END TAP PAYMENT API FUNCTIONS
// END TAP PAYMENT API FUNCTIONS

// //// twilio endpoints
export const sendVerificationOtp = body => {
  return post('/api/send-otp', body);
};

export const verifyOtp = body => {
  return post('/api/verify-otp', body);
};

// Fetch cart transaction line items from the local API endpoint.
//
// See `server/api/cart-transaction-line-items.js` to see what data should
// be sent in the body.
export const cartTransactionLineItems = body => {
  return post('/api/cart-transaction-line-items', body);
};

export const onUpdateCategoriesProfile = body => {
  return post('/api/update-categories-profile', body);
};

// Transaction messages (Negotiated model) -- see server/api/messages/
export const fetchTransactionMessages = (transactionId, { before, limit } = {}) => {
  const params = new URLSearchParams({ transactionId });
  if (before) params.set('before', before);
  if (limit) params.set('limit', String(limit));
  return get(`/api/messages?${params.toString()}`);
};

export const sendTransactionMessage = body => {
  return post('/api/messages', body);
};

export const sendTransactionQuote = body => {
  return post('/api/messages/quote', body);
};

export const onUpdateDealCategoriesProfile = body => {
  return post('/api/update-deal-categories-profile', body);
};

export const onSendPushNotification = body => {
  return post('/api/one-signal-push-notification', body);
};

export const onSendNotification = body => {
  return post('/api/add-notification', body);
};

export const onGetNotifications = body => {
  return post('/api/get-notification-history', body);
};

export const onGetNotificationsV2 = body => {
  return post('/api/get-notification-history-v2', body);
};

export const onSendBuzzNotification = body => {
  return post('/api/send-buzz-notification', body);
};

export const onVerifyQRCode = body => {
  return post('/api/verify-qr-code', body);
};

export const onCreateCustomer = body => {
  return post('/api/create-customer', body);
};

export const sendMailToUser = body => {
  return post('/api/send_mail_to_user', body);
};

// Sign the NOW Offers Merchant Agreement. The server validates the typed name
// against the registered legal name, generates a signed PDF, records the
// signature on the merchant's profile, and emails the PDF.
//
// See `server/api/merchant-agreement/sign-merchant-agreement.js`.
export const onSignMerchantAgreement = body => {
  return post('/api/sign-merchant-agreement', JSON.stringify(body), {
    headers: { 'Content-Type': 'application/json' },
  });
};

export const updatePaymentInDB = body => {
  return post('/api/updatePaymentInDB', body);
};

export const deleteCard = body => {
  return post('/api/deleteCard', body);
};

export const updateServicesAndDeals = body => {
  return post('/api/update-services-and-deals', body);
};

export const addUserPasswordHash = body => {
  return post('/api/add_user_password_hash', body);
};

export const sendWhatsAppMessage = body => {
  return post('/api/send-whatsapp-message', body);
};

export const onWhatsappTransaction = body => {
  return post(`/api/whatsapp-transactions`, body);
};

export const onCheckPhoneNumberExists = body => {
  return post(`/api/check_phone_number_exists`, body);
};

export const onUpdateUserPasswordHash = body => {
  return post(`/api/update_user_passwordHash`, body);
};

export const onFetchUserByEmail = body => {
  return post('/api/fetch-user-by-email', body);
};

export const onSendImageMessage = body => {
  return post('/api/send-image-message', body);
};

// TAP PAYMENT - COMMENTED OUT (Replaced with STC Bank)
// onCreateAuthorize is Tap-specific for payment authorization
export const onCreateAuthorize = body => {
  return post('/api/create-authorize', body);
};

export const onFetchAds = body => {
  return post('/api/ads', body);
};

export const onCreateAds = body => {
  return post('/api/ads/create', body);
};
export const onSubscriptionPlan = () => {
  return get('/api/ads/subscriptionPlan');
};
export const onGetAcceptedAds = body => {
  return post('/api/ads/accepted', body);
};

export const onGetAdsByListingType = body => {
  return post('/api/ads/accepted/listing-type', body);
};
export const onUpdateAdClick = body => {
  return post('/api/ads/updateAdClick', body);
};
export const onAddRewardPoints = body => {
  return post('/api/ads/reward-points', body);
};

export const onFetchAdsById = id => {
  return get(`/api/ads/${id}`, id);
};
export const onUpdateAdAndAddTx = body => {
  return post('/api/ads/add-transaction-and-update-ad', body);
};

export const onGetObjectsFromAlgolia = body => {
  return post('/api/get-objects-from-algolia', body);
};
export const onGetUserPoints = async userId => {
  try {
    const response = await get(`/api/ads/user-points/${userId}`);
    console.log('API response:', response); // Add debug log
    return response;
  } catch (error) {
    console.error('Error fetching user points:', error);
    throw error;
  }
};

export const onGetUserRewardHistory = (userId, limit = 10, offset = 0) => {
  return get(`/api/ads/reward-history/${userId}?limit=${limit}&offset=${offset}`);
};

export const onGetMerchantAdRewardStats = merchantId => {
  console.log('merchantId', merchantId);
  return get(`/api/ads/merchant-reward-stats/${merchantId}`);
};
// TAP PAYMENT - COMMENTED OUT (Replaced with STC Bank - use onRetrieveStcPayment instead)
export const onRetrieveAuthorize = body => {
  return post('/api/retrieve-authorize', body);
};
export const onUpdateTransactionMetadata = body => {
  return post('/api/update-transaction-metadata', body);
};

// ratings and reviews
export const onAddReview = body => {
  return post('/api/add_review', body);
};

// STC Pay endpoints
export const onInitiateStcAuthorize = body => {
  return post('/api/stc/initiate', body);
};

export const onRetrieveStcPayment = body => {
  return post('/api/stc/status', body);
};

export const getPaymentReference = transactionId => {
  return get(`/api/payment-reference?transactionId=${encodeURIComponent(transactionId)}`);
};

export const getTimeline = (transactionId, merchantId) => {
  return get(
    `/api/ledger/timeline?transactionId=${encodeURIComponent(
      transactionId
    )}&merchantId=${encodeURIComponent(merchantId)}`
  );
};

export const onConfirmStcPayment = body => {
  return post('/api/stc/confirm', body);
};

export const onRefundStcPayment = body => {
  return post('/api/stc/refund', body);
};

export const onRecordDisputeCancelled = body => {
  return post('/api/transactions/record-dispute-cancelled', body);
};

// Merchant Ledger endpoints
export const fetchLedgerBalance = async merchantId => {
  return get(`/api/ledger/balance?merchantId=${merchantId}`);
};

export const fetchLedgerEntries = async (
  merchantId,
  { limit = 50, offset = 0, entry_type, status } = {}
) => {
  let url = `/api/ledger/entries?merchantId=${merchantId}&limit=${limit}&offset=${offset}`;
  if (entry_type) url += `&entry_type=${entry_type}`;
  if (status) url += `&status=${status}`;
  return get(url);
};

export const fetchLedgerSummary = async (merchantId, startDate, endDate) => {
  return get(
    `/api/ledger/summary?merchantId=${merchantId}&startDate=${startDate}&endDate=${endDate}`
  );
};

// Manual payout requests removed - payouts are now handled automatically by cron jobs

export const fetchPayoutStatus = async (payoutId, merchantId) => {
  return get(`/api/ledger/payouts/status/${payoutId}?merchantId=${merchantId}`);
};

export const onGetListingReviews = body => {
  return post('/api/get_listing_reviews', body);
};

export const onGetUserReviews = body => {
  return post('/api/get_user_reviews', body);
};
export const onGetOwnReviews = body => {
  return post('/api/get_own_reviews', body);
};

export const onGetStaffReviews = body => {
  return post('/api/get_staff_reviews', body);
};
export const onGetDetailedListingRatings = body => {
  return post('/api/get_detailed_ratings_for_listing', body);
};
export const onGetDetailedUserRatings = body => {
  return post('/api/get_detailed_ratings_for_user', body);
};

// AWS
export const onUploadFileToAWS = body => {
  return post('/api/upload-file-to-aws', body);
};

export const onGeneratedPresignedUrls = body => {
  return post('/api/generate_presigned_urls_S3', body);
};

export const onContactUs = body => {
  return post('/api/contact-us', body);
};

export const onVerifyCaptcha = body => {
  return post('/api/verify-captcha', body);
};

// Analytics
export const onAddAnalytics = body => {
  return post('/api/add-analytics', body);
};

export const onFetchAnalytics = body => {
  return post('/api/fetch-analytics-web', body);
};
export const onShowUser = body => {
  return post('/api/show-user', body);
};

// Request Payout
export const onGetTotalBalance = body => {
  return post('/api/get-total-balance', body);
};
export const onRequestPayout = body => {
  return post('/api/request-payout', body);
};
export const onGetRequestedPayouts = body => {
  return post('/api/get-requested-payouts', body);
};

export const deleteUser = body => {
  return post('/api/delete-user', body);
};

export const onCheckOngoingTransactions = body => {
  return post('/api/check-ongoing-transactions', body);
};

// export const updateReview = (body) => {
//   return post('/api/updateReview', body)
// }
